<template>
  <div class="placement-picker">
    <div class="placement-picker__modes" role="radiogroup">
      <button type="button" role="radio" :aria-checked="value.mode === 'all'" class="placement-picker__mode" :class="{ _active: value.mode === 'all' }" @click="setMode('all')">
        <span class="placement-picker__radio"></span>
        <span>
          <span class="placement-picker__mode-title">{{ $t("satellites.placement.all") }}</span>
          <span class="placement-picker__mode-text">{{ $t("satellites.placement.all_hint", { count: satellites.length }) }}</span>
        </span>
      </button>
      <button type="button" role="radio" :aria-checked="value.mode === 'selected'" class="placement-picker__mode" :class="{ _active: value.mode === 'selected' }" @click="setMode('selected')">
        <span class="placement-picker__radio"></span>
        <span>
          <span class="placement-picker__mode-title">{{ $t("satellites.placement.selected") }}</span>
          <span class="placement-picker__mode-text">{{ $t("satellites.placement.selected_hint") }}</span>
        </span>
      </button>
    </div>

    <div v-if="value.mode === 'selected'" class="placement-picker__targets">
      <template v-if="groups.length">
        <div class="placement-picker__section">{{ $t("satellites.placement.groups") }}</div>
        <label v-for="group in groups" :key="group.id" class="placement-picker__target">
          <input type="checkbox" :checked="value.group_ids.includes(group.id)" @change="toggle('group_ids', group.id)" />
          <span class="placement-picker__box"><SatIcon name="check" :size="12" /></span>
          <span class="placement-picker__name">{{ group.name }}</span>
          <span class="placement-picker__meta">{{ pl("satellites.counts.satellites", membersOf(group.id)) }}</span>
        </label>
      </template>

      <template v-if="standalone.length">
        <div class="placement-picker__section">{{ $t("satellites.placement.standalone") }}</div>
        <label v-for="satellite in standalone" :key="satellite.id" class="placement-picker__target">
          <input type="checkbox" :checked="value.satellite_ids.includes(satellite.id)" @change="toggle('satellite_ids', satellite.id)" />
          <span class="placement-picker__box"><SatIcon name="check" :size="12" /></span>
          <span class="placement-picker__name">{{ satName(satellite) }}</span>
          <span class="placement-picker__meta sat-mono">{{ satellite.ip || "—" }}</span>
        </label>
      </template>

      <div v-if="!groups.length && !standalone.length" class="placement-picker__empty">{{ $t("satellites.placement.nothing") }}</div>
      <div v-if="hiddenGrouped" class="placement-picker__note">{{ $t("satellites.placement.grouped_note") }}</div>
    </div>

    <div class="placement-picker__summary" :class="{ _warn: !servingCount }">
      <SatIcon :name="servingCount ? 'server' : 'alert'" :size="14" />
      {{ servingCount ? pl("satellites.placement.summary", servingCount) : $t("satellites.placement.summary_none") }}
    </div>
  </div>
</template>
<script>
import SatIcon from "@/components/Satellites/SatIcon.vue";
import { formatMixin } from "@/components/Satellites/format";

/**
 * Which satellites serve a domain. v-model: { mode: "all" | "selected", group_ids, satellite_ids }.
 * Only satellites without a group can be picked one by one: a group decides for its satellites.
 */
export default {
  name: "PlacementPicker",
  components: { SatIcon },
  mixins: [formatMixin],
  props: {
    value: {
      type: Object,
      required: true,
    },
    groups: {
      type: Array,
      required: true,
    },
    // All satellites of the user, with their groups
    satellites: {
      type: Array,
      required: true,
    },
  },
  computed: {
    standalone() {
      return this.satellites.filter((satellite) => !satellite.group);
    },
    hiddenGrouped() {
      return this.satellites.length > this.standalone.length && this.groups.length > 0;
    },
    servingCount() {
      if (this.value.mode === "all") return this.satellites.length;
      const groups = new Set(this.value.group_ids);
      const satellites = new Set(this.value.satellite_ids);
      return this.satellites.filter((s) => (s.group ? groups.has(s.group.id) : satellites.has(s.id))).length;
    },
  },
  methods: {
    membersOf(groupId) {
      return this.satellites.filter((s) => s.group && s.group.id === groupId).length;
    },
    setMode(mode) {
      this.$emit("input", { ...this.value, mode });
    },
    toggle(field, id) {
      const list = this.value[field].includes(id) ? this.value[field].filter((item) => item !== id) : [...this.value[field], id];
      this.$emit("input", { ...this.value, [field]: list });
    },
  },
};
</script>
<style lang="scss" scoped>
.placement-picker__modes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}

.placement-picker__mode {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--sat-border);
  border-radius: 12px;
  text-align: left;
  color: var(--sat-text);
  background: var(--sat-card);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: var(--sat-accent);
  }

  &._active {
    border-color: var(--sat-accent);
    background: var(--sat-accent-soft);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px var(--sat-accent);
  }
}

.placement-picker__radio {
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border: 2px solid var(--sat-border);
  border-radius: 50%;
  background: var(--sat-card);

  ._active > & {
    border-color: var(--sat-accent);

    &::after {
      content: "";
      position: absolute;
      inset: 3px;
      border-radius: 50%;
      background: var(--sat-accent);
    }
  }
}

.placement-picker__mode-title {
  display: block;
  font-size: 14px;
  font-weight: 600;
}

.placement-picker__mode-text {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--sat-muted);
}

.placement-picker__targets {
  margin-top: 12px;
  padding: 6px;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--sat-border);
  border-radius: 12px;
}

.placement-picker__section {
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sat-muted);
}

.placement-picker__target {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--sat-hover);
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
}

.placement-picker__box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid var(--sat-border);
  border-radius: 5px;
  color: transparent;
  transition: background 0.12s, border-color 0.12s;

  input:checked + & {
    border-color: var(--sat-accent);
    color: #fff;
    background: var(--sat-accent);
  }

  input:focus-visible + & {
    box-shadow: 0 0 0 2px var(--sat-accent-soft);
  }
}

.placement-picker__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.placement-picker__meta {
  margin-left: auto;
  font-size: 12px;
  color: var(--sat-muted);
  white-space: nowrap;
}

.placement-picker__empty,
.placement-picker__note {
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.45;
  color: var(--sat-muted);
}

.placement-picker__summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sat-text-2);

  &._warn {
    color: var(--sat-warn);
  }
}
</style>
